(() => {
  const configNode = document.getElementById('hw-config');
  if (!configNode || window.haiyiWishlist) return;
  const config = JSON.parse(configNode.textContent);
  const track = (name, category) => { try { window.haiyiWishlistTelemetry?.record(name, category); } catch {} };
  const text = (key, values = {}) => Object.entries(values).reduce((s, [k, v]) => s.replaceAll(`[${k}]`, v), config.strings[key]);
  const root = () => (window.Shopify?.routes?.root || config.root).replace(/\/?$/, '/');
  const key = `haiyi-wishlist-v1:${config.shop}`;
  let page = document.querySelector('[data-wishlist-page]');
  const toast = document.querySelector('[data-wishlist-toast]');
  let items = [], revision = 0, adding = false, timer, undo, lastToggle;
  const products = new Map();
  const id = value => /^\d+$/.test(String(value)) ? String(value) : null;
  const handle = value => {
    if (typeof value !== 'string') return null;
    try {
      const decoded = decodeURIComponent(value);
      if (!decoded || decoded === '.' || decoded === '..' || /[\s\u0000-\u001f\u007f-\u009f/\\?#%]/u.test(decoded)) return null;
      encodeURIComponent(decoded); // Reject unpaired surrogates before storing or constructing URLs.
      return decoded;
    } catch { return null; }
  };
  const money = value => new Intl.NumberFormat(document.documentElement.lang || 'en', {style: 'currency', currency: config.currency}).format(value / 100);
  const productURL = item => `${root()}products/${encodeURIComponent(item.handle)}`;
  const status = message => { if (page) page.querySelector('[data-wishlist-status]').textContent = message; };

  function read() {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const data = JSON.parse(raw);
    if (data.version !== 1 || !Array.isArray(data.items)) throw Error(text('storage_invalid'));
    const seen = new Set();
    return data.items.map(item => {
      if (!id(item.productId) || !handle(item.handle) || typeof item.title !== 'string'
        || (item.variantId !== null && !id(item.variantId)) || seen.has(id(item.productId))) throw Error(text('storage_invalid'));
      seen.add(id(item.productId));
      return {...item, productId: id(item.productId), handle: handle(item.handle), variantId: item.variantId && id(item.variantId)};
    });
  }

  const observedChatRoots = new WeakSet();
  function positionToast() {
    const chatButtons = [];
    function visitChat(root) {
      if (!root) return;
      if (!observedChatRoots.has(root)) {
        new MutationObserver(positionToast).observe(root, {childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'style']});
        observedChatRoots.add(root);
      }
      chatButtons.push(...root.querySelectorAll('button.activator, button[part~="activator"]'));
      root.querySelectorAll('*').forEach(element => { if (element.shadowRoot) visitChat(element.shadowRoot); });
    }
    visitChat(document.querySelector('shopify-chat')?.shadowRoot);
    if (toast.hidden || !matchMedia('(max-width: 768px)').matches) {
      toast.style.removeProperty('--hw-toast-offset');
      return;
    }
    let offset = 16;
    const obstacles = [document.querySelector('sticky-atc-panel'), ...chatButtons];
    obstacles.forEach(element => {
      if (!element) return;
      const rect = element.getBoundingClientRect(), style = getComputedStyle(element);
      if (rect.width && rect.height && rect.top >= 0 && rect.top < innerHeight
        && style.visibility !== 'hidden' && style.opacity !== '0') {
        offset = Math.max(offset, innerHeight - rect.top + 12);
      }
    });
    toast.style.setProperty('--hw-toast-offset', `${offset}px`);
  }

  function show(message, action = null, error = false) {
    clearTimeout(timer);
    undo = action;
    toast.querySelector('[data-wishlist-message]').textContent = message;
    toast.querySelector('[data-wishlist-undo]').hidden = !action;
    toast.querySelector('[data-wishlist-view]').hidden = Boolean(action);
    toast.hidden = false;
    positionToast();
    // Keep errors and undo available until dismissed. Short success notices pause on focus/hover.
    if (!error && !action) timer = setTimeout(() => { if (!toast.matches(':focus-within, :hover')) toast.hidden = true; }, 3500);
  }

  function mutate(change, clear = false) {
    try {
      const next = change(clear ? [] : read());
      localStorage.setItem(key, JSON.stringify({version: 1, items: next}));
      items = next;
      sync();
      if (page) render();
      return true;
    } catch (error) {
      track('error', 'storage_write');
      show(text('storage_error'), null, true);
      status(text('storage_error'));
      return false;
    }
  }

  function sync() {
    document.querySelectorAll('[data-wishlist-toggle]').forEach(button => {
      const saved = items.some(item => item.productId === button.dataset.productId);
      button.setAttribute('aria-pressed', String(saved));
      button.setAttribute('aria-label', text(saved ? 'remove_named' : 'save_named', {title: button.dataset.title}));
    });
    document.querySelectorAll('[data-wishlist-link]').forEach(link => {
      link.dataset.hasItems = String(items.length > 0);
      link.setAttribute('aria-label', text('header_count', {count: items.length}));
      const count = link.querySelector('[data-wishlist-count]');
      count.textContent = items.length;
      count.hidden = !items.length;
    });
  }

  function remove(productId, opener) {
    const previous = items.find(item => item.productId === productId);
    if (!previous) return;
    lastToggle = opener;
    if (mutate(current => current.filter(item => item.productId !== productId))) {
      show(text('removed'), () => mutate(current => current.some(item => item.productId === productId) ? current : [...current, previous]));
    }
  }

  function toggle(button) {
    if (adding) return;
    try { items = read(); } catch { track('error', 'storage_read'); show(text('storage_error'), null, true); return; }
    if (items.some(item => item.productId === button.dataset.productId)) return remove(button.dataset.productId, button);
    const item = {productId: id(button.dataset.productId), handle: handle(button.dataset.handle), title: button.dataset.title, variantId: id(button.dataset.variantId)};
    if (!item.productId || !item.handle) { show(text('load_error'), null, true); return; }
    if (button.dataset.productForm) {
      const form = document.getElementById(button.dataset.productForm);
      const picker = form?.closest('.shopify-section')?.querySelector('variant-picker');
      const chosen = picker?.getSelectedOptions?.();
      const variant = picker?.variant;
      item.variantId = id(form?.querySelector('[name="id"]')?.value);
      // Do not save the old hidden ID while a new combination is still loading.
      if (!item.variantId || (picker && (!variant || String(variant.id) !== item.variantId
        || chosen?.some((value, index) => value === null || value !== variant.options[index])))) {
        show(text('select_first'), null, true); return;
      }
    } else {
      const card = button.closest('product-card');
      const link = card?.querySelector('.js-prod-link');
      const selected = link && new URL(link.href, location.href).searchParams.get('variant');
      if (selected && button.dataset.variantChoices === '1') item.variantId = id(selected);
    }
    lastToggle = button;
    if (mutate(current => [...current, item])) { track('save_success'); show(text('saved')); }
  }

  async function json(url, options) {
    const controller = new AbortController(), timeout = setTimeout(() => controller.abort(), 12000);
    try {
      const response = await fetch(url, {...options, signal: controller.signal});
      let data;
      try { data = await response.json(); }
      catch (error) { if (response.ok) throw error; data = {}; }
      if (!response.ok) throw Object.assign(Error(data.description || data.message || text('load_error')), {status: response.status});
      return data;
    } finally { clearTimeout(timeout); }
  }

  async function load(item) {
    try {
      const product = await json(`${productURL(item)}.js`);
      if (String(product.id) !== item.productId) return {missing: true};
      return {product};
    } catch (error) {
      return error.status === 404 || error.status === 410 ? {missing: true} : {error: true};
    }
  }

  async function loadMany(snapshot) {
    const results = new Array(snapshot.length);
    let next = 0;
    await Promise.all(Array.from({length: Math.min(4, snapshot.length)}, async () => {
      while (next < snapshot.length) {
        const index = next++, item = snapshot[index];
        results[index] = {item, state: await load(item)};
      }
    }));
    return results;
  }

  function variantFor(item, product) {
    return product?.variants.find(v => String(v.id) === item.variantId)
      || (!item.variantId && product?.variants.length === 1 ? product.variants[0] : null);
  }

  function canBuy(product, variant) {
    return Boolean(product && variant?.available && !product.requires_selling_plan
      && !variant.requires_selling_plan && (!variant.quantity_rule || (variant.quantity_rule.min <= 1 && variant.quantity_rule.increment <= 1)));
  }

  function card(item, index) {
    const fragment = page.querySelector('[data-wishlist-item]').content.cloneNode(true);
    const article = fragment.querySelector('article');
    article.dataset.productId = item.productId;
    const state = products.get(item.productId) || {}, product = state.product;
    const variant = variantFor(item, product);
    const url = `${productURL(item)}${variant ? `?variant=${variant.id}` : ''}`;
    const set = (selector, value) => { article.querySelector(selector).textContent = value; };
    const title = product?.title || item.title;
    article.querySelectorAll('[data-item-link], [data-item-title], [data-item-details]').forEach(a => { a.href = url; });
    set('[data-item-title]', title);
    const removeButton = article.querySelector('[data-item-remove]');
    removeButton.disabled = adding;
    removeButton.dataset.wishlistRemove = item.productId;
    removeButton.setAttribute('aria-label', text('remove_named', {title}));
    const image = article.querySelector('[data-item-image]');
    const src = variant?.featured_image?.src || product?.featured_image;
    // Product JSON is remote input; allow only HTTP(S) image URLs.
    if (src && /^https?:$/.test(new URL(src, location.href).protocol)) { image.src = src; image.alt = title; }
    else image.hidden = true;
    const price = variant?.price ?? product?.price;
    set('[data-item-price]', price == null ? '' : `${!variant && product?.price_varies ? text('from') + ' ' : ''}${money(price)}`);
    const compare = variant?.compare_at_price;
    set('[data-item-compare]', compare > price ? money(compare) : '');
    const select = article.querySelector('[data-item-select]');
    select.id = `hw-variant-${index}`;
    select.dataset.wishlistVariant = item.productId;
    const label = article.querySelector('[data-item-label]');
    label.htmlFor = select.id;
    label.textContent = text('option');
    select.setAttribute('aria-label', text('option_named', {title}));
    const option = (value, name) => { const el = document.createElement('option'); el.value = value; el.textContent = name; select.append(el); };
    option('', text('choose_option'));
    for (const v of product?.variants || []) option(v.id, `${v.title === 'Default Title' ? text('standard_option') : v.title}${v.available ? '' : ' · ' + text('sold_out')}`);
    select.value = variant?.id || '';
    select.disabled = !product || adding;
    const add = article.querySelector('[data-item-add]');
    add.dataset.wishlistAdd = item.productId;
    add.disabled = adding || !canBuy(product, variant);
    add.textContent = text('add');
    let note = '';
    if (state.missing) note = text('unavailable');
    else if (state.error) note = text('load_error');
    else if (product && !variant) note = item.variantId ? text('variant_missing') : text('choose_option');
    else if (variant && !variant.available) note = text('sold_out');
    else if (variant && !canBuy(product, variant)) note = text('product_options');
    set('[data-item-status]', note);
    if (state.missing || (variant && !variant.available)) {
      set('[data-item-badge]', state.missing ? text('unavailable') : text('sold_out'));
      article.querySelector('[data-item-badge]').hidden = false;
    }
    const details = article.querySelector('[data-item-details]');
    details.hidden = !product || canBuy(product, variant);
    if (state.error) { add.disabled = adding; add.textContent = text('retry'); add.dataset.wishlistRetry = ''; }
    return fragment;
  }

  function paint() {
    if (!page) return;
    const focused = document.activeElement;
    const focusId = focused?.closest('[data-product-id]')?.dataset.productId;
    const focusAttribute = focused?.matches('[data-item-select]') ? 'data-item-select' : focused?.matches('[data-item-remove]') ? 'data-item-remove' : focused?.matches('[data-item-add]') ? 'data-item-add' : null;
    const list = page.querySelector('[data-wishlist-items]');
    list.replaceChildren(...items.map(card));
    page.querySelector('[data-wishlist-empty]').hidden = items.length !== 0;
    page.querySelector('[data-wishlist-clear]').hidden = !items.length;
    page.querySelector('[data-wishlist-clear]').disabled = adding;
    page.querySelector('[data-wishlist-summary]').hidden = !items.length;
    let total = 0, count = 0;
    items.forEach(item => {
      const product = products.get(item.productId)?.product, variant = variantFor(item, product);
      if (canBuy(product, variant)) { total += variant.price; count++; }
    });
    page.querySelector('[data-wishlist-subtotal]').textContent = text('subtotal', {count, total: money(total)});
    const all = page.querySelector('[data-wishlist-add-all]');
    all.textContent = adding ? text('adding') : text('add_all', {count});
    all.disabled = adding || !count;
    if (focusAttribute && focusId) {
      const next = [...list.children].find(node => node.dataset.productId === focusId)?.querySelector(`[${focusAttribute}]`);
      if (next && !next.disabled) next.focus({preventScroll: true});
      else if (document.activeElement === document.body) page.querySelector('#hw-title').focus({preventScroll: true});
    }
  }

  async function refresh(snapshot) {
    const generation = ++revision;
    if (page) page.querySelector('[data-wishlist-items]').setAttribute('aria-busy', 'true');
    const states = await loadMany(snapshot);
    if (generation !== revision) return false;
    if (states.some(({state}) => state.error)) track('error', 'product_load');
    products.clear(); states.forEach(({item, state}) => products.set(item.productId, state));
    if (page) page.querySelector('[data-wishlist-items]').setAttribute('aria-busy', 'false');
    paint();
    return true;
  }

  async function render() {
    paint();
    await refresh(items);
  }

  async function addToCart(productId) {
    if (adding) return;
    adding = true;
    status(text('checking'));
    paint();
    let sent = false, confirmed = false, failureCategory = 'storage_read';
    try {
      items = read();
      const snapshot = items.filter(item => !productId || item.productId === productId);
      // Always revalidate price, availability and the explicitly saved variant before POST.
      const fresh = await loadMany(snapshot);
      if (fresh.some(({state}) => state.error)) track('error', 'product_load');
      let lines = fresh.filter(({item, state}) => canBuy(state.product, variantFor(item, state.product)));
      fresh.forEach(({item, state}) => products.set(item.productId, state));
      paint();
      if (!lines.length) { status(text('nothing_to_add')); return; }
      failureCategory = 'cart_read';
      const before = await json(`${root()}cart.js`);
      failureCategory = 'storage_read';
      const latest = read();
      lines = lines.filter(({item}) => latest.some(saved => saved.productId === item.productId && saved.handle === item.handle && saved.variantId === item.variantId));
      const skipped = snapshot.length - lines.length;
      if (!lines.length) { status(text('nothing_to_add')); return; }
      config.currency = before.currency || config.currency;
      const drawer = document.querySelector('cart-drawer');
      const sectionIds = drawer ? drawer.getSectionsToRender().map(section => section.section) : ['cart-icon-bubble'];
      sent = true;
      failureCategory = 'cart_uncertain';
      const result = await json(`${root()}cart/add.js`, {
        method: 'POST', headers: {'Content-Type': 'application/json', Accept: 'application/json'},
        body: JSON.stringify({items: lines.map(({item, state}) => ({id: Number(variantFor(item, state.product).id), quantity: 1})), sections: sectionIds, sections_url: location.pathname})
      });
      // A parsed response must identify every submitted item before counting success.
      if (!Array.isArray(result.items) || !lines.every(({item, state}) => result.items.some(added =>
        String(added.variant_id ?? added.id) === String(variantFor(item, state.product).id) && added.quantity >= 1))) throw Error(text('request_uncertain'));
      confirmed = true;
      track('cart_success');
      failureCategory = 'cart_display';
      const after = await json(`${root()}cart.js`);
      // Bundled sections can be null even after a successful add. Never resubmit to repair the UI.
      const valid = result.sections && sectionIds.every(sectionId => {
        const html = result.sections[sectionId];
        if (typeof html !== 'string') return false;
        const selector = drawer?.getSectionsToRender().find(section => section.section === sectionId)?.selector || '.shopify-section';
        return Boolean(new DOMParser().parseFromString(html, 'text/html').querySelector(selector));
      });
      if (!valid) throw Error(text('display_error'));
      if (drawer) {
        await drawer.renderContents(result, false);
        drawer.open(document.activeElement);
      } else {
        const bubble = document.getElementById('cart-icon-bubble');
        if (bubble) bubble.innerHTML = result.sections['cart-icon-bubble'];
      }
      lines.forEach(({item, state}) => {
        const variantId = Number(variantFor(item, state.product).id);
        const sum = cart => cart.items.filter(line => line.variant_id === variantId).reduce((n, line) => n + line.quantity, 0);
        const oldQuantity = sum(before), newQuantity = sum(after);
        document.dispatchEvent(new CustomEvent(oldQuantity ? 'on:line-item:change' : 'on:cart:add', {bubbles: true, detail: {cart: after, variantId, oldQuantity, newQuantity}}));
      });
      const message = text('added', {count: lines.length}) + (skipped ? ' ' + text('skipped', {count: skipped}) : '');
      status(message);
      // Keep add feedback in the page so it never overlaps the cart drawer.
      toast.hidden = true;
    } catch (error) {
      track('error', !confirmed && sent && error.status >= 400 && error.status < 500 ? 'cart_rejected' : failureCategory);
      status(confirmed ? text('display_error') : sent ? text('request_uncertain') : error.message || text('load_error'));
      if (sent) {
        const link = document.createElement('a'); link.href = `${root()}cart`; link.textContent = ' ' + text('review_cart');
        page.querySelector('[data-wishlist-status]').append(link);
      }
    } finally { adding = false; paint(); }
  }

  document.addEventListener('click', event => {
    const button = event.target.closest('[data-wishlist-toggle]');
    if (button) { event.preventDefault(); toggle(button); return; }
    const removeButton = event.target.closest('[data-wishlist-remove]');
    if (removeButton) { if (!adding) remove(removeButton.dataset.wishlistRemove, removeButton); return; }
    if (event.target.closest('[data-wishlist-clear]')) {
      if (!adding && confirm(text('confirm_clear')) && mutate(() => [], true)) {
        status(''); page.querySelector('#hw-title').focus({preventScroll: true});
      }
    } else if (event.target.closest('[data-wishlist-add-all]')) addToCart();
    else if (event.target.closest('[data-wishlist-retry]')) render();
    else if (event.target.closest('[data-wishlist-add]')) addToCart(event.target.closest('[data-wishlist-add]').dataset.wishlistAdd);
    else if (event.target.closest('[data-wishlist-undo]')) {
      if (undo?.()) {
        toast.hidden = true; sync();
        if (lastToggle?.isConnected) lastToggle.focus();
        else page?.querySelector('#hw-title').focus({preventScroll: true});
      }
    } else if (event.target.closest('[data-wishlist-close]')) {
      toast.hidden = true; if (lastToggle?.isConnected) lastToggle.focus();
    }
  });
  document.addEventListener('change', event => {
    if (!event.target.matches('[data-wishlist-variant]')) return;
    const productId = event.target.dataset.wishlistVariant, variantId = id(event.target.value);
    const old = items;
    if (!mutate(current => current.map(item => item.productId === productId ? {...item, variantId} : item))) { items = old; paint(); }
  });
  document.addEventListener('keydown', event => {
    if (event.key === 'Escape' && !toast.hidden && toast.contains(document.activeElement)) {
      toast.hidden = true; if (lastToggle?.isConnected) lastToggle.focus();
    }
  });
  toast.addEventListener('focusin', () => clearTimeout(timer));
  toast.addEventListener('mouseenter', () => clearTimeout(timer));
  window.addEventListener('resize', positionToast);
  window.addEventListener('scroll', positionToast, {passive: true});
  customElements.whenDefined('shopify-chat').then(positionToast);
  customElements.whenDefined('shopify-agent-runtime').then(positionToast);
  document.addEventListener('shopify:section:load', () => { page = document.querySelector('[data-wishlist-page]'); initialize(); });
  // Predictive search, recommendations, quick-add and Theme editor insert cards dynamically.
  new MutationObserver(records => {
    if (!toast.hidden) positionToast();
    if (records.some(record => [...record.addedNodes].some(node => node.nodeType === 1 && (node.matches('[data-wishlist-toggle], [data-wishlist-link]') || node.querySelector('[data-wishlist-toggle], [data-wishlist-link]'))))) sync();
  }).observe(document.body, {childList: true, subtree: true});
  window.addEventListener('storage', event => { if (event.key === key || event.key === null) initialize(); });
  window.addEventListener('pageshow', event => { if (event.persisted) initialize(); });
  function initialize() {
    try { items = read(); sync(); if (page) render(); }
    catch { track('error', 'storage_read'); show(text('storage_error'), null, true); status(text('storage_error')); if (page) page.querySelector('[data-wishlist-clear]').hidden = false; }
    if (page) { try { window.haiyiWishlistTelemetry?.view(); } catch {} }
  }
  window.haiyiWishlist = {key};
  initialize();
})();
